import type { CaptureState } from "./ui-types";

export type RecordingPhase =
  | "idle"
  | "permission"
  | "connecting"
  | "recording"
  | "stopping"
  | "completed"
  | "error";

export type RecordingConnection = "standing-by" | "connected" | "buffering";

export type RecordingStartupStage =
  | "idle"
  | "requesting-permission"
  | "preparing-audio"
  | "provider-ready";

export type RecordingLifecycle = {
  phase: RecordingPhase;
  captureState: CaptureState;
  connection: RecordingConnection;
  error: string | null;
  isStarting: boolean;
  startupStage: RecordingStartupStage;
  reconnectAttempt: number;
};

export type RecordingLifecycleEvent =
  | { type: "start.requested" }
  | { type: "permission.granted" }
  | { type: "permission.denied"; message: string }
  | { type: "socket.connected" }
  | { type: "provider.ready" }
  | { type: "provider.failed"; message: string }
  | { type: "capture.ready" }
  | { type: "start.cancelled" }
  | { type: "stop.requested" }
  | { type: "reconnect.scheduled"; attempt: number }
  | { type: "reconnect.reset" }
  | { type: "reconnect.exhausted"; message: string }
  | { type: "start.failed" }
  | { type: "start.settled" }
  | { type: "capture.changed"; state: CaptureState }
  | { type: "connection.changed"; connection: RecordingConnection }
  | { type: "error.changed"; message: string | null }
  | { type: "starting.changed"; isStarting: boolean }
  | { type: "startup.changed"; stage: RecordingStartupStage }
  | { type: "reset" };

export const initialRecordingLifecycle: RecordingLifecycle = {
  phase: "idle",
  captureState: "ready",
  connection: "standing-by",
  error: null,
  isStarting: false,
  startupStage: "idle",
  reconnectAttempt: 0
};

const phaseForCaptureState = (state: CaptureState): RecordingPhase => {
  if (state === "listening") return "recording";
  if (state === "processing") return "stopping";
  if (state === "review" || state === "done") return "completed";
  if (state === "interrupted") return "error";
  return "idle";
};

export function recordingLifecycleReducer(
  current: RecordingLifecycle,
  event: RecordingLifecycleEvent
): RecordingLifecycle {
  switch (event.type) {
    case "start.requested":
      return {
        ...initialRecordingLifecycle,
        phase: "permission",
        isStarting: true,
        startupStage: "requesting-permission"
      };
    case "permission.granted":
      return {
        ...current,
        phase: "connecting",
        startupStage: "preparing-audio"
      };
    case "permission.denied":
      return {
        ...current,
        phase: "error",
        captureState: "ready",
        connection: "standing-by",
        error: event.message,
        isStarting: false,
        startupStage: "idle"
      };
    case "socket.connected":
      return {
        ...current,
        phase:
          current.captureState === "listening"
            ? "recording"
            : current.captureState === "processing"
              ? "stopping"
              : "connecting",
        connection: "connected"
      };
    case "provider.ready":
      return { ...current, startupStage: "provider-ready", reconnectAttempt: 0 };
    case "provider.failed":
      return { ...current, phase: "error", error: event.message };
    case "capture.ready":
      return {
        ...current,
        phase: "recording",
        captureState: "listening",
        connection: "connected",
        isStarting: false,
        startupStage: "provider-ready",
        reconnectAttempt: 0
      };
    case "start.cancelled":
      return {
        ...current,
        phase: "stopping",
        connection: "standing-by",
        error: "Recording startup was cancelled."
      };
    case "stop.requested":
      return { ...current, phase: "stopping", captureState: "processing" };
    case "reconnect.scheduled":
      return {
        ...current,
        phase: current.captureState === "processing" ? "stopping" : "connecting",
        connection: "buffering",
        reconnectAttempt: event.attempt
      };
    case "reconnect.reset":
      return { ...current, reconnectAttempt: 0 };
    case "reconnect.exhausted":
      return {
        ...current,
        phase: "error",
        connection: "buffering",
        error: event.message
      };
    case "start.failed":
      return { ...current, phase: "error" };
    case "start.settled":
      return { ...current, isStarting: false, startupStage: "idle" };
    case "capture.changed":
      return {
        ...current,
        phase: phaseForCaptureState(event.state),
        captureState: event.state
      };
    case "connection.changed":
      return { ...current, connection: event.connection };
    case "error.changed":
      return { ...current, error: event.message };
    case "starting.changed":
      return { ...current, isStarting: event.isStarting };
    case "startup.changed":
      return { ...current, startupStage: event.stage };
    case "reset":
      return initialRecordingLifecycle;
  }
}

export const MAX_RECONNECT_ATTEMPTS = 4;
export const MAX_FINALISATION_ATTEMPTS = 30;

export function canStartRecording(
  lifecycle: Pick<RecordingLifecycle, "phase" | "captureState">
): boolean {
  return (
    lifecycle.captureState === "ready" &&
    (lifecycle.phase === "idle" || lifecycle.phase === "error")
  );
}

export function canStopRecording(
  lifecycle: Pick<RecordingLifecycle, "phase" | "captureState">
): boolean {
  return (
    lifecycle.captureState === "listening" ||
    lifecycle.phase === "permission" ||
    lifecycle.phase === "connecting"
  );
}

export function nextReconnect(
  completedAttempts: number
): { attempt: number; delayMs: number } | null {
  const attempt = completedAttempts + 1;
  if (attempt > MAX_RECONNECT_ATTEMPTS) return null;
  return { attempt, delayMs: Math.min(4_000, 500 * 2 ** (attempt - 1)) };
}

export function orderedPendingPackets<T extends { sequence: number }>(
  packets: Iterable<T>
): T[] {
  return [...packets].sort((left, right) => left.sequence - right.sequence);
}

export function mergeOrderedPackets<T extends { sequence: number }>(
  primary: Iterable<T>,
  fallback: Iterable<T>
): T[] {
  const unique = new Map<number, T>();
  for (const packet of primary) unique.set(packet.sequence, packet);
  for (const packet of fallback) {
    if (!unique.has(packet.sequence)) unique.set(packet.sequence, packet);
  }
  return orderedPendingPackets(unique.values());
}

export function captureSealPending(input: {
  stopSent: boolean;
  pendingSourcePackets: number;
  pendingAnalysisPackets: number;
}): boolean {
  return (
    !input.stopSent ||
    input.pendingSourcePackets > 0 ||
    input.pendingAnalysisPackets > 0
  );
}
